import sha256 from 'crypto-js/sha256';
import storage from 'local-storage-fallback';
import { CustomError, isNetworkError } from '../../common/errors';
import Log from '../../common/logging';
import Result from '../../common/result';
import { NOT_FOUND } from '../../services/events/statusEventPayload';
import validationService from '../../services/validation';

const getSessionStorageKey = (did: string, networkAddress: string) => sha256(`${did}:${networkAddress}`).toString();

const cacheStatus = (did: string, networkAddress: string, status: string): void => {
  try {
    const sessionStorageKey = getSessionStorageKey(did, networkAddress);
    storage.setItem(sessionStorageKey, status);
  } catch (error) {
    Log.error('Error setting item from storage: ', error as Error);
  }
};

const getCachedStatus = (did: string, networkAddress: string): string | null => {
  try {
    const sessionStorageKey = getSessionStorageKey(did, networkAddress);
    return storage.getItem(sessionStorageKey);
  } catch (error) {
    Log.error('Error getting to storage: ', error as Error);
    // If storage is not available, then fallback to no cache solution
    return null;
  }
};

const clearCachedStatus = (did: string, networkAddress: string): void => {
  try {
    const sessionStorageKey = getSessionStorageKey(did, networkAddress);
    storage.removeItem(sessionStorageKey);
  } catch (error) {
    Log.error('Error clearing item in storage: ', error as Error);
  }
};

const getStatus = async (did: string, networkAddress: string): Promise<Result<CustomError, string>> => {
  const getStatusResult = await validationService.getStatus({
    did,
    networkAddress,
  });

  if (getStatusResult.isFailed() && !getStatusResult.failedWith(isNetworkError(404))) {
    return Result.failed(getStatusResult.failure());
  }

  const status = getStatusResult.isSuccessful() ? getStatusResult.success().status : NOT_FOUND;
  return Result.successful(status);
};

export default {
  cacheStatus,
  getCachedStatus,
  getStatus,
  clearCachedStatus,
};
